import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import BoxesPage from "@/pages/BoxesPage";
import BoxDetailPage from "@/pages/BoxDetailPage";
import StockTakingPage from "@/pages/StockTakingPage";
import StockTakingResultsPage from "@/pages/StockTakingResultsPage";
import DailyOpsPage from "@/pages/DailyOpsPage";
import BillsPage from "@/pages/BillsPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import ReconciliationPage from "@/pages/ReconciliationPage";
import CustomersPage from "@/pages/CustomersPage";
import SuppliersPage from "@/pages/SuppliersPage";
import NotificationsPage from "@/pages/NotificationsPage";
import UsersPage from "@/pages/UsersPage";
import BoxMergePage from "@/pages/BoxMergePage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import PurchaseOrdersPage from "@/pages/PurchaseOrdersPage";
import ItemsPage from "@/pages/ItemsPage";
import SalesmenPage from "@/pages/SalesmenPage";
import ReportsPage from "@/pages/ReportsPage";
import BoxHistoryPage from "@/pages/BoxHistoryPage";
import DataSafetyPage from "@/pages/DataSafetyPage";

function ProtectedRoute({ children, allowedRoles, denyRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="text-lg">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  const roleOk = !allowedRoles || allowedRoles.includes(user.role) || (user.role === 'analyst' && allowedRoles.includes('admin'));
  if (!roleOk || (denyRoles && denyRoles.includes(user.role))) return <Navigate to="/dashboard" replace />;
  return <AppShell>{children}</AppShell>;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="text-lg">Loading...</div></div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/boxes" element={<ProtectedRoute allowedRoles={['admin','store_manager','stock_manager']}><BoxesPage /></ProtectedRoute>} />
      <Route path="/boxes/:boxId" element={<ProtectedRoute allowedRoles={['admin','store_manager','stock_manager']}><BoxDetailPage /></ProtectedRoute>} />
      <Route path="/stock-taking" element={<ProtectedRoute allowedRoles={['admin','store_manager','stock_manager','billing_executive']}><StockTakingPage /></ProtectedRoute>} />
      <Route path="/stock-taking-results" element={<ProtectedRoute allowedRoles={['admin','store_manager']}><StockTakingResultsPage /></ProtectedRoute>} />
      <Route path="/daily-ops" element={<ProtectedRoute allowedRoles={['admin','store_manager','stock_manager']}><DailyOpsPage /></ProtectedRoute>} />
      <Route path="/bills" element={<ProtectedRoute allowedRoles={['admin','store_manager','billing_executive','stock_manager']}><BillsPage /></ProtectedRoute>} />
      <Route path="/approvals" element={<ProtectedRoute allowedRoles={['admin','store_manager']}><ApprovalsPage /></ProtectedRoute>} />
      <Route path="/reconciliation" element={<ProtectedRoute allowedRoles={['admin','store_manager']}><ReconciliationPage /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute allowedRoles={['admin','store_manager']} denyRoles={['analyst']}><CustomersPage /></ProtectedRoute>} />
      <Route path="/suppliers" element={<ProtectedRoute allowedRoles={['admin','store_manager']}><SuppliersPage /></ProtectedRoute>} />
      <Route path="/box-merge" element={<ProtectedRoute allowedRoles={['admin','store_manager','stock_manager']}><BoxMergePage /></ProtectedRoute>} />
      <Route path="/purchase-orders" element={<ProtectedRoute allowedRoles={['admin','store_manager']}><PurchaseOrdersPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute allowedRoles={['admin','store_manager']}><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute allowedRoles={['admin','store_manager']}><NotificationsPage /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute allowedRoles={['admin']} denyRoles={['analyst']}><UsersPage /></ProtectedRoute>} />
      <Route path="/items" element={<ProtectedRoute allowedRoles={['admin','store_manager']}><ItemsPage /></ProtectedRoute>} />
      <Route path="/salesmen" element={<ProtectedRoute allowedRoles={['admin','store_manager']}><SalesmenPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute allowedRoles={['admin','store_manager']}><ReportsPage /></ProtectedRoute>} />
      <Route path="/box-history" element={<ProtectedRoute allowedRoles={['admin','store_manager','stock_manager','billing_executive']}><BoxHistoryPage /></ProtectedRoute>} />
      <Route path="/data-safety" element={<ProtectedRoute allowedRoles={['admin']}><DataSafetyPage /></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
